import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Layer, Rect, Stage } from 'react-konva';

import { ELEMENT_TYPES } from '../core/documentSchema';
import ImageElement from '../elements/image/ImageElement';
import TextElement from '../elements/text/TextElement';
import ShapeElement from '../elements/shape/ShapeElement';

const MAX_EXPORT_EDGE = 8192;

const waitFrames = (count = 2) =>
  new Promise((resolve) => {
    let remaining = count;
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) {
        resolve();
        return;
      }
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });

const ExportDocumentStage = ({ width, height, background, elements, onStageReady }) => {
  const stageRef = useRef(null);

  useEffect(() => {
    if (stageRef.current) {
      onStageReady?.(stageRef.current);
    }
  }, [onStageReady, width, height, elements]);

  return (
    <Stage ref={stageRef} width={width} height={height} listening={false}>
      <Layer listening={false}>
        <Rect x={0} y={0} width={width} height={height} fill={background?.color || '#ffffff'} listening={false} />
        {elements.map((element) => {
          if (element.type === ELEMENT_TYPES.TEXT) {
            return <TextElement key={element.id} element={element} interactive={false} listening={false} />;
          }
          if (element.type === ELEMENT_TYPES.SHAPE) {
            return <ShapeElement key={element.id} element={element} interactive={false} listening={false} />;
          }
          return (
            <ImageElement
              key={element.id}
              element={element}
              interactive={false}
              listening={false}
              cacheMaxEdge={Number.POSITIVE_INFINITY}
              filterDebounceMs={0}
            />
          );
        })}
      </Layer>
    </Stage>
  );
};

const stageToExactBlob = (stage, mimeType, pixelRatio, outputSize) =>
  new Promise((resolve, reject) => {
    try {
      const source = stage.toCanvas({ pixelRatio });
      const canvas = document.createElement('canvas');
      canvas.width = outputSize.width;
      canvas.height = outputSize.height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to export image'))), mimeType, 1);
    } catch (error) {
      reject(error);
    }
  });

const stageToBlob = (stage, mimeType, pixelRatio) =>
  new Promise((resolve, reject) => {
    if (!stage) {
      reject(new Error('Stage is not available'));
      return;
    }

    try {
      const dataUrl = stage.toDataURL({ mimeType, pixelRatio, quality: 1 });
      fetch(dataUrl)
        .then((response) => response.blob())
        .then((blob) => {
          if (!blob) {
            reject(new Error('Failed to export image'));
            return;
          }
          resolve(blob);
        })
        .catch(reject);
    } catch (error) {
      reject(error);
    }
  });

export const exportDocumentToBlob = async (state, mimeType = 'image/png') => {
  const width = Math.max(1, Math.round(state.width || 0));
  const height = Math.max(1, Math.round(state.height || 0));
  const target = state.outputSize?.width ? state.outputSize.width / width : 1;
  const pixelRatio = Math.min(target, MAX_EXPORT_EDGE / Math.max(width, height));

  const host = document.createElement('div');
  host.style.cssText =
    'position:fixed;left:-10000px;top:0;width:0;height:0;overflow:hidden;pointer-events:none;opacity:0;';
  document.body.appendChild(host);

  const root = createRoot(host);
  let stageResolve;
  const stageReady = new Promise((resolve) => {
    stageResolve = resolve;
  });

  try {
    root.render(
      <ExportDocumentStage
        width={width}
        height={height}
        background={state.background}
        elements={state.elements || []}
        onStageReady={stageResolve}
      />,
    );

    const stage = await stageReady;
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    await waitFrames(2);
    stage.find('Image').forEach((node) => {
      if (node.filters()?.length) {
        try {
          node.cache({ pixelRatio: 1 });
        } catch {
          node.filters([]);
        }
      }
    });
    stage.batchDraw();
    await waitFrames(1);

    const outputSize = state.outputSize;
    if (outputSize?.width && Math.max(outputSize.width, outputSize.height) <= MAX_EXPORT_EDGE) {
      return await stageToExactBlob(stage, mimeType, pixelRatio, outputSize);
    }
    return await stageToBlob(stage, mimeType, pixelRatio);
  } finally {
    root.unmount();
    host.remove();
  }
};
